import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { eventJournalApi } from '@entities/eventJournal/api';
import { useGetAllQueryParams } from '@entities/eventJournal/lib/hooks';
import { currentEventId } from '@entities/eventJournal/model/queries';
import { TJournalEventId } from '@entities/eventJournal/model/types';
import { handleStatusError } from '@shared/helpers/handleStatusError';
import { useNuqsQueryState } from '@shared/hooks';
import { submitAdapter } from '@widgets/EventJournal/model/submitAdapter';
import { IEventJournalModalSchema } from '@widgets/EventJournal/model/types';

interface IUseSendJournalEventModalForm {
    onSuccessCb?: () => void;
    modalMode: string;
}

export const useSendJournalEventModalForm = ({
    onSuccessCb,
    modalMode,
}: IUseSendJournalEventModalForm) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { adaptedQueryParams } = useGetAllQueryParams();
    const { state: currentEventIdQueryState } = useNuqsQueryState(currentEventId);

    const sendFormMutation = useMutation({
        mutationFn: (vars: { data: FormData; id: TJournalEventId }) => {
            if (modalMode === 'create') {
                return eventJournalApi.createJournalEvent(vars.data);
            }
            if (modalMode === 'edit') {
                return eventJournalApi.updateJournalEvent(vars);
            }
            return null;
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: eventJournalApi.getJournalEventsListQueryOptions({
                    queryParams: adaptedQueryParams,
                }).queryKey,
            });
        },
        onSuccess: () => onSuccessCb?.(),
        onError: (error: AxiosError<{ code: number; message: string; status: string }>) => {
            console.error('error in update event: ', error);
            if (error?.status === 500) {
                handleStatusError(error?.message, t('button.got_it.text'));
            }
            if (error?.response?.data?.status === 'error') {
                handleStatusError(error?.response?.data, t('button.got_it.text'));
            }
        },
    });

    const handleSubmit = async (formData: IEventJournalModalSchema) => {
        const formDataToSubmit = submitAdapter(formData);
        if (modalMode === 'create') {
            await sendFormMutation.mutateAsync({ data: formDataToSubmit, id: null });
        }
        if (modalMode === 'edit') {
            await sendFormMutation.mutateAsync({
                id: Number(currentEventIdQueryState),
                data: formDataToSubmit,
            });
        }
    };

    return {
        handleSubmit,
        isPending: sendFormMutation.isPending,
    };
};
